import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Flags } from './parseFlags';
import { parseFlags } from './parseFlags';
import { ComboCounter } from './ComboCounter';
import { ChartRendererDebug } from './ChartRendererDebug';
import {
  NOTE_THICKNESS,
  TRACE_NOTE_THICKNESS,
  NOTE_CORNER_RADIUS,
  GRID_LINE_THICKNESS,
  COLUMN_SPACING,
  BEATS_PER_COLUMN,
  LANE_FLAG_MAX_VALUE,
  CHART_PADDING,
  FLOAT_COMPARISON_EPSILON,
  COMBO_COUNT_PADDING,
  FLICK_ARROW_SIZE,
  FLICK_ARROW_COLOR,
  HOLD_END_COLUMN_ADJUST,
  NOTE_COLOR_SINGLE,
  NOTE_COLOR_TRACE,
  NOTE_COLOR_HOLD,
  HOLD_SEGMENT_ALPHA,
  NOTE_COLOR_FLICK,
  CHART_AREA_BACKGROUND_COLOR,
  NOTE_COLOR_DEFAULT,
  FEVER_SECTION_COLOR,
  FEVER_SECTION_ALPHA,
  HOLD_NOTE_KEY_DIGITS,
  HOLD_NOTE_KEY_PRECISION
} from './constants';
import type { ChartData, Note, PreProcessedChartFileSoA, ParsedNote, HoldLink } from '~/types';

interface HoldSegmentDrawConfig {
  segmentOwningNoteUidForDebugText: number;
  thisSegmentActualStartBeat: number;
  thisSegmentActualEndBeat: number;
  thisSegmentStartLaneL: number;
  thisSegmentStartLaneR: number;
  thisSegmentEndLaneL: number;
  thisSegmentEndLaneR: number;
  thisSegmentStartTimeSecondsForDebugText: number;
  noteWhoseHoldsDefineTicks: ParsedNote;
  allParsedNotesMapForTicks: Map<number, ParsedNote>;
  overallHoldSequenceInitialBeat: number;
  overallHoldSequenceFinalBeat: number;
  overallHoldSequenceInitialLaneL: number;
  overallHoldSequenceInitialLaneR: number;
  overallHoldSequenceFinalLaneL: number;
  overallHoldSequenceFinalLaneR: number;
}

export class ChartRenderer {
  app: Application;
  laneWidth = 175;
  beatSpacing = 64;
  referenceBpm = 120;
  private bpms: ChartData['Bpms'] = [];
  private chartOffset: number = 0;
  private comboCounter: ComboCounter | undefined;
  private _bpmTextStyle!: TextStyle;
  private _cumulativeYOffsetCache: Map<number, number> = new Map();
  private _columnBoundaries: number[] = [];
  private _targetColumnHeight: number = 0;

  constructor(private debug = false) {
    this.app = new Application();
    this.debug = debug;
    this._initializeBpmTextStyle();
  }

  private _initializeBpmTextStyle() {
    this._bpmTextStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 12,
      fontWeight: 'bold',
      align: 'left',
      fill: 0xffffff,
      padding: 0
    });
  }

  async initialize() {
    await this.app.init({ antialias: true, autoDensity: true });
    this.app.renderer.events.autoPreventDefault = true;
  }

  private _processChartData(preProcessedDataSoA: PreProcessedChartFileSoA): {
    parsedNotesMap: Map<number, ParsedNote>;
    rootParsedNotes: ParsedNote[];
    maxBeat: number;
    sortedParsedNotes: ParsedNote[];
  } {
    const [
      processedNoteUIDs,
      processedNoteFlagsNums,
      processedNoteJustSeconds, // This is in seconds
      processedNoteResolvedHoldsArrays,
      _initialBpm,
      _initialBeatSignatureTuple,
      processedMaxBeat,
      rootNoteUIDsData,
      _chartBpms,
      _chartOffset
    ] = preProcessedDataSoA;

    const parsedNotesMap = new Map<number, ParsedNote>();
    const allParsedNotesList: ParsedNote[] = [];

    for (let i = 0; i < processedNoteUIDs.length; i++) {
      const uid = processedNoteUIDs[i];
      const flagsNum = processedNoteFlagsNums[i];
      const justSecondsValue = processedNoteJustSeconds[i];
      const resolvedHoldsFromTuple = processedNoteResolvedHoldsArrays[i];

      const currentFlags = parseFlags(flagsNum);
      const holdsArray: HoldLink[] = resolvedHoldsFromTuple.map((linkValueFromTuple: number) => {
        if (linkValueFromTuple < 0) {
          return { type: 'uid', uid: -linkValueFromTuple };
        } else {
          const holdEndTimeSeconds = linkValueFromTuple;
          return { type: 'beat', beat: holdEndTimeSeconds };
        }
      });
      const parsedNote: ParsedNote = {
        uid: uid,
        just: justSecondsValue, // Store as seconds
        holds: holdsArray,
        flags: currentFlags
      };
      parsedNotesMap.set(uid, parsedNote);
      allParsedNotesList.push(parsedNote);
    }
    const sortedParsedNotes = [...allParsedNotesList].sort((a, b) => a.just - b.just);

    const rootParsedNotes: ParsedNote[] = [];
    for (const uid of rootNoteUIDsData) {
      const note = parsedNotesMap.get(uid);
      if (note) {
        rootParsedNotes.push(note);
      }
    }

    return {
      parsedNotesMap,
      rootParsedNotes,
      maxBeat: processedMaxBeat,
      sortedParsedNotes
    };
  }

  renderChart(
    preProcessedDataSoA: PreProcessedChartFileSoA,
    sectionTimes?: number[],
    feverSectionNumber?: number // 1-indexed
  ) {
    this.app.stage.removeChildren();
    this._cumulativeYOffsetCache.clear();

    const [
      _noteUIDs,
      _noteFlagsNums,
      _noteJustSecondsData, // This is in seconds
      _noteResolvedHoldsArrays,
      initialBpm,
      initialBeatSignatureTuple,
      _maxBeatFromTuple,
      _rootNoteUIDs,
      chartBpmsData,
      chartOffsetData
    ] = preProcessedDataSoA;
    this.bpms = chartBpmsData;
    this.chartOffset = chartOffsetData;
    this.referenceBpm = initialBpm;

    const { parsedNotesMap, rootParsedNotes, maxBeat, sortedParsedNotes } =
      this._processChartData(preProcessedDataSoA);

    this.comboCounter = new ComboCounter(sortedParsedNotes, parsedNotesMap, this.bpms);

    const firstBeatTimingInfoForGrid = initialBeatSignatureTuple
      ? { N: initialBeatSignatureTuple[0], D: initialBeatSignatureTuple[1] }
      : undefined;

    this._targetColumnHeight = BEATS_PER_COLUMN * (this.beatSpacing + GRID_LINE_THICKNESS);
    this._columnBoundaries = this._computeColumnBoundaries(maxBeat);
    const numColumns = this._columnBoundaries.length - 1;

    const chartAreaWidth = (this.laneWidth + COLUMN_SPACING) * numColumns - COLUMN_SPACING;

    this.app.renderer.resize(
      chartAreaWidth + 2 * CHART_PADDING,
      this._targetColumnHeight + 2 * CHART_PADDING
    );

    const feverSectionBackgroundsContainer = this._drawFeverSectionBackgrounds(
      sectionTimes,
      feverSectionNumber,
      maxBeat,
      numColumns
    );

    this.app.stage.addChild(feverSectionBackgroundsContainer);

    const columnBackgrounds = new Graphics();

    for (let i = 0; i < numColumns; i++) {
      const columnStartBeat = this._columnBoundaries[i];
      const columnEndBeat = this._columnBoundaries[i + 1];
      const columnXStart = i * (this.laneWidth + COLUMN_SPACING) + CHART_PADDING;
      const thisColumnHeight = this._getCumulativeScaledOffset(columnStartBeat, columnEndBeat);

      const rectYForBg = this._targetColumnHeight - thisColumnHeight + CHART_PADDING;
      const heightForBg = Math.max(0, thisColumnHeight);

      columnBackgrounds.rect(columnXStart, rectYForBg, this.laneWidth, heightForBg);
    }
    columnBackgrounds.fill({ color: CHART_AREA_BACKGROUND_COLOR, alpha: 0.1 });
    this.app.stage.addChild(columnBackgrounds);

    const grid = this._drawGrid(firstBeatTimingInfoForGrid, maxBeat);
    this.app.stage.addChild(grid);

    const bpmContainer = new Container();
    this._drawBpmInfo(bpmContainer);
    this.app.stage.addChild(bpmContainer);

    const notesContainer = new Container();
    rootParsedNotes.forEach((rootNote) => {
      this._drawNoteChain(rootNote, parsedNotesMap, notesContainer);
    });
    this.app.stage.addChild(notesContainer);
    this._drawMeasureComboCounts(maxBeat, firstBeatTimingInfoForGrid?.N || 4, this.app.stage);
  }

  private _drawGrid(beatSignature: { N: number; D: number } | undefined, maxBeatValue: number) {
    const gridContainer = new Container();
    const denominator = beatSignature?.D || 4;

    const sortedBpms = [...this.bpms].sort((a, b) => a.Time - b.Time);
    const bpmChangeBeatOffsets = this._getBpmChangeBeatOffsets(sortedBpms, maxBeatValue);

    for (const { startBeat, endBeat, beatOffset } of bpmChangeBeatOffsets) {
      let beat = startBeat;
      while (beat <= endBeat + FLOAT_COMPARISON_EPSILON) {
        const localBeatIndex = Math.round(beat - beatOffset);
        const yPos = this._getBeatYOffset(beat);
        const barLineGraphics = new Graphics();
        barLineGraphics
          .rect(this._getBeatXOffset(beat), yPos, this.laneWidth, GRID_LINE_THICKNESS)
          .fill(localBeatIndex % denominator === 0 ? 0xeeeeee : 0x444444);
        gridContainer.addChild(barLineGraphics);

        if (this.debug) {
          ChartRendererDebug.drawGridTimeDebugInfo({
            container: gridContainer,
            beatIndex: beat,
            xPos: this._getBeatXOffset(beat),
            yPos,
            laneWidth: this.laneWidth,
            gridLineThickness: GRID_LINE_THICKNESS,
            bpms: this.bpms,
            chartOffset: this.chartOffset
          });
        }
        beat += 1;
      }
    }
    return gridContainer;
  }

  private _getBpmChangeBeatOffsets(
    sortedBpms: ChartData['Bpms'],
    maxBeat: number
  ): { startBeat: number; endBeat: number; beatOffset: number }[] {
    const segments: { startBeat: number; endBeat: number; beatOffset: number }[] = [];

    let prevBpmChangeBeat = 0;

    for (let i = 0; i < sortedBpms.length; i++) {
      const bpmEvent = sortedBpms[i];
      const chartRelativeTime = bpmEvent.Time - this.chartOffset;
      if (chartRelativeTime <= FLOAT_COMPARISON_EPSILON) continue;

      const bpmChangeBeat = ChartRenderer.convertChartSecondsToBeat(
        chartRelativeTime,
        this.bpms,
        this.chartOffset
      );

      if (bpmChangeBeat > prevBpmChangeBeat && bpmChangeBeat < maxBeat) {
        segments.push({
          startBeat: prevBpmChangeBeat,
          endBeat: bpmChangeBeat - FLOAT_COMPARISON_EPSILON,
          beatOffset: prevBpmChangeBeat
        });
        prevBpmChangeBeat = bpmChangeBeat;
      }
    }

    segments.push({
      startBeat: prevBpmChangeBeat,
      endBeat: maxBeat,
      beatOffset: prevBpmChangeBeat
    });

    return segments;
  }

  private _getNormalizedLanePosition(flagValue: number): number {
    return (flagValue / LANE_FLAG_MAX_VALUE) * this.laneWidth;
  }

  private _drawFeverSectionBackgrounds(
    sectionTimes: number[] | undefined,
    feverSectionNumber: number | undefined,
    maxBeat: number,
    numColumns: number
  ): Container {
    const feverSectionGraphicsContainer = new Container();

    if (sectionTimes && sectionTimes.length > 0 && feverSectionNumber && feverSectionNumber > 0) {
      const feverSectionIndex = feverSectionNumber - 1; // 0-indexed

      if (feverSectionIndex < 5) {
        const feverStartTimeSeconds =
          feverSectionIndex === 0 ? 0 : sectionTimes[feverSectionIndex - 1] / 1000;
        let feverEndTimeSeconds: number;

        // If it's the 5th section (index 4), its end time is the end of the song.
        if (feverSectionIndex === 4) {
          feverEndTimeSeconds = ChartRenderer.convertBeatToChartSeconds(
            maxBeat,
            this.bpms,
            this.chartOffset
          );
        } else {
          feverEndTimeSeconds = sectionTimes[feverSectionIndex] / 1000;
        }
        if (feverEndTimeSeconds > feverStartTimeSeconds) {
          let feverStartBeat = ChartRenderer.convertChartSecondsToBeat(
            feverStartTimeSeconds,
            this.bpms,
            this.chartOffset
          );
          let feverEndBeat = ChartRenderer.convertChartSecondsToBeat(
            feverEndTimeSeconds,
            this.bpms,
            this.chartOffset
          );

          feverStartBeat = Math.max(0, feverStartBeat);
          feverEndBeat = Math.min(feverEndBeat, maxBeat);

          if (feverEndBeat > feverStartBeat) {
            for (let i = 0; i < numColumns; i++) {
              const columnFirstBeat = this._columnBoundaries[i];
              const columnLastBeat = this._columnBoundaries[i + 1];

              const segmentStartBeat = Math.max(feverStartBeat, columnFirstBeat);
              const segmentEndBeat = Math.min(feverEndBeat, columnLastBeat);

              if (segmentStartBeat < segmentEndBeat) {
                const xPos = i * (this.laneWidth + COLUMN_SPACING) + CHART_PADDING;
                const offsetToStart = this._getCumulativeScaledOffset(
                  columnFirstBeat,
                  segmentStartBeat
                );
                const offsetToEnd = this._getCumulativeScaledOffset(
                  columnFirstBeat,
                  segmentEndBeat
                );
                const yRectBottomValue = this._targetColumnHeight - offsetToStart + CHART_PADDING;
                const yRectTopValue = this._targetColumnHeight - offsetToEnd + CHART_PADDING;
                const rectHeight = yRectBottomValue - yRectTopValue;

                if (rectHeight > 0) {
                  const segmentGraphic = new Graphics();
                  segmentGraphic
                    .rect(xPos, yRectTopValue, this.laneWidth, rectHeight)
                    .fill({ color: FEVER_SECTION_COLOR, alpha: FEVER_SECTION_ALPHA });
                  feverSectionGraphicsContainer.addChild(segmentGraphic);
                }
              }
            }
          }
        }
      }
    }
    return feverSectionGraphicsContainer;
  }

  private _drawSingleNoteOrHoldHead(note: ParsedNote, container: Container) {
    const actualBeat = ChartRenderer.convertChartSecondsToBeat(
      note.just,
      this.bpms,
      this.chartOffset
    );
    const noteGridLineTopX = this._getBeatXOffset(actualBeat);
    const noteGridLineTopY = this._getBeatYOffset(actualBeat);

    const normL1 = this._getNormalizedLanePosition(note.flags.L1);
    const normR1 = this._getNormalizedLanePosition(note.flags.R1);
    const displayL1 = Math.min(normL1, normR1);
    const displayR1 = Math.max(normL1, normR1);
    const headWidth = displayR1 - displayL1;
    const headGraphics = new Graphics();
    let currentNoteThickness = NOTE_THICKNESS;
    let headColor: number;

    switch (note.flags.noteType) {
      case 'Single':
        headColor = NOTE_COLOR_SINGLE;
        break;
      case 'Trace':
        headColor = NOTE_COLOR_TRACE;
        currentNoteThickness = TRACE_NOTE_THICKNESS;
        break;
      case 'Hold':
        headColor = NOTE_COLOR_HOLD;
        break;
      case 'Flick':
        headColor = NOTE_COLOR_FLICK;
        break;
      default:
        headColor = NOTE_COLOR_DEFAULT;
        break;
    }

    const rectX = noteGridLineTopX + displayL1;
    const yCenteringOffset = (GRID_LINE_THICKNESS - currentNoteThickness) / 2;
    const rectY = noteGridLineTopY + yCenteringOffset;

    headGraphics.roundRect(rectX, rectY, headWidth, currentNoteThickness, NOTE_CORNER_RADIUS);
    headGraphics.fill(headColor);

    if (note.flags.noteType === 'Flick') {
      const noteDrawnTopY = rectY;
      const arrowCenterX = noteGridLineTopX + displayL1 + headWidth / 2;
      const arrowBaseY = noteDrawnTopY - 2;
      const arrowTipY = arrowBaseY - FLICK_ARROW_SIZE;
      const arrowHalfWidth = FLICK_ARROW_SIZE / 2;

      headGraphics
        .moveTo(arrowCenterX, arrowTipY)
        .lineTo(arrowCenterX - arrowHalfWidth, arrowBaseY)
        .lineTo(arrowCenterX + arrowHalfWidth, arrowBaseY)
        .closePath()
        .fill(FLICK_ARROW_COLOR);
    }
    container.addChild(headGraphics);

    if (this.debug) {
      const noteDrawnBottomY = rectY + currentNoteThickness;
      ChartRendererDebug.drawNoteDebugInfo({
        note,
        comboCounter: this.comboCounter,
        container,
        noteDrawnBottomY,
        noteCenterX: noteGridLineTopX + displayL1 + headWidth / 2
      });
    }
  }

  private _drawIndividualHoldSegment(config: HoldSegmentDrawConfig, container: Container) {
    const {
      segmentOwningNoteUidForDebugText,
      thisSegmentActualStartBeat,
      thisSegmentActualEndBeat,
      thisSegmentStartLaneL,
      thisSegmentStartLaneR,
      thisSegmentEndLaneL,
      thisSegmentEndLaneR,
      thisSegmentStartTimeSecondsForDebugText
    } = config;

    if (this.debug && this.comboCounter) {
      const beatLineX = this._getBeatXOffset(thisSegmentActualStartBeat);
      const beatLineY = this._getBeatYOffset(thisSegmentActualStartBeat);
      const segmentStartXInLane = Math.min(thisSegmentStartLaneL, thisSegmentStartLaneR);

      ChartRendererDebug.drawHoldSegmentDebugInfo({
        noteUid: segmentOwningNoteUidForDebugText,
        segmentInitialStartTimeSeconds: thisSegmentStartTimeSecondsForDebugText,
        comboCounter: this.comboCounter,
        container,
        segmentStartX: beatLineX,
        segmentStartY: beatLineY + GRID_LINE_THICKNESS / 2,
        segmentStartLanePos: segmentStartXInLane
      });
    }

    let currentPieceBeatStart = thisSegmentActualStartBeat;
    const totalOriginalSegmentDuration = thisSegmentActualEndBeat - thisSegmentActualStartBeat;

    while (currentPieceBeatStart < thisSegmentActualEndBeat) {
      const startColumnIndexOfPiece = this._getColumnIndexForBeat(currentPieceBeatStart);
      const startOfNextColumn =
        this._columnBoundaries[startColumnIndexOfPiece + 1] ?? thisSegmentActualEndBeat;

      let pieceRenderEndBeat: number;
      let nextLoopStartBeat: number;

      if (thisSegmentActualEndBeat >= startOfNextColumn) {
        pieceRenderEndBeat = startOfNextColumn - HOLD_END_COLUMN_ADJUST;
        nextLoopStartBeat = startOfNextColumn;
      } else {
        pieceRenderEndBeat = thisSegmentActualEndBeat;
        nextLoopStartBeat = thisSegmentActualEndBeat;
      }

      const isVeryShortSegment = Math.abs(totalOriginalSegmentDuration) < FLOAT_COMPARISON_EPSILON;
      const safeTotalOriginalSegmentDuration = isVeryShortSegment
        ? 1
        : totalOriginalSegmentDuration;
      const startFraction =
        (currentPieceBeatStart - thisSegmentActualStartBeat) / safeTotalOriginalSegmentDuration;
      const endFraction =
        (pieceRenderEndBeat - thisSegmentActualStartBeat) / safeTotalOriginalSegmentDuration;

      let pieceStartLaneL: number;
      let pieceStartLaneR: number;
      let pieceEndLaneL: number;
      let pieceEndLaneR: number;

      if (isVeryShortSegment) {
        pieceStartLaneL = thisSegmentStartLaneL;
        pieceStartLaneR = thisSegmentStartLaneR;
        pieceEndLaneL = thisSegmentEndLaneL;
        pieceEndLaneR = thisSegmentEndLaneR;
      } else {
        pieceStartLaneL =
          thisSegmentStartLaneL + (thisSegmentEndLaneL - thisSegmentStartLaneL) * startFraction;
        pieceStartLaneR =
          thisSegmentStartLaneR + (thisSegmentEndLaneR - thisSegmentStartLaneR) * startFraction;
        pieceEndLaneL =
          thisSegmentStartLaneL + (thisSegmentEndLaneL - thisSegmentStartLaneL) * endFraction;
        pieceEndLaneR =
          thisSegmentStartLaneR + (thisSegmentEndLaneR - thisSegmentStartLaneR) * endFraction;
      }

      const xPieceStartAbs = this._getBeatXOffset(currentPieceBeatStart);
      const yPieceStartAbs = this._getBeatYOffset(currentPieceBeatStart);
      const xPieceEndAbs = this._getBeatXOffset(pieceRenderEndBeat);
      const yPieceEndAbs = this._getBeatYOffset(pieceRenderEndBeat);

      const displayPieceStartL = Math.min(pieceStartLaneL, pieceStartLaneR);
      const displayPieceStartR = Math.max(pieceStartLaneL, pieceStartLaneR);
      const displayPieceEndL = Math.min(pieceEndLaneL, pieceEndLaneR);
      const displayPieceEndR = Math.max(pieceEndLaneL, pieceEndLaneR);

      const yOffset = GRID_LINE_THICKNESS / 2;
      const pieceGraphics = new Graphics();
      pieceGraphics
        .poly([
          xPieceStartAbs + displayPieceStartL,
          yPieceStartAbs + yOffset,
          xPieceStartAbs + displayPieceStartR,
          yPieceStartAbs + yOffset,
          xPieceEndAbs + displayPieceEndR,
          yPieceEndAbs + yOffset,
          xPieceEndAbs + displayPieceEndL,
          yPieceEndAbs + yOffset
        ])
        .fill({ color: NOTE_COLOR_HOLD, alpha: HOLD_SEGMENT_ALPHA });
      container.addChild(pieceGraphics);

      if (this.debug) {
        const visualPieceStartBeat = currentPieceBeatStart;
        const visualPieceEndBeat = pieceRenderEndBeat;

        ChartRendererDebug.drawHoldSegmentTicksDebug({
          container,
          noteWhoseHoldsDefineTicks: config.noteWhoseHoldsDefineTicks,
          allParsedNotesMapForTicks: config.allParsedNotesMapForTicks,
          overallHoldSequenceInitialBeat: config.overallHoldSequenceInitialBeat,
          overallHoldSequenceFinalBeat: config.overallHoldSequenceFinalBeat,
          overallHoldSequenceInitialLaneL: config.overallHoldSequenceInitialLaneL,
          overallHoldSequenceInitialLaneR: config.overallHoldSequenceInitialLaneR,
          overallHoldSequenceFinalLaneL: config.overallHoldSequenceFinalLaneL,
          overallHoldSequenceFinalLaneR: config.overallHoldSequenceFinalLaneR,
          visualPieceStartBeat,
          visualPieceEndBeat,
          bpms: this.bpms,
          chartOffset: this.chartOffset,
          getBeatXOffset: (beat) => this._getBeatXOffset(beat),
          getBeatYOffset: (beat) => this._getBeatYOffset(beat),
          gridLineThickness: GRID_LINE_THICKNESS
        });
      }
      currentPieceBeatStart = nextLoopStartBeat;
    }
  }

  private _drawHoldSegmentsForNote(
    note: ParsedNote,
    allParsedNotesMap: Map<number, ParsedNote>,
    container: Container
  ): ParsedNote | undefined {
    let currentSegmentStartTimeSeconds = note.just;

    const overallInitialLaneL = this._getNormalizedLanePosition(note.flags.L1);
    const overallInitialLaneR = this._getNormalizedLanePosition(note.flags.R1);
    const overallFinalLaneL = this._getNormalizedLanePosition(note.flags.L2);
    const overallFinalLaneR = this._getNormalizedLanePosition(note.flags.R2);

    let currentSegmentStartLaneL = overallInitialLaneL;
    let currentSegmentStartLaneR = overallInitialLaneR;

    let nextNoteInChain: ParsedNote | undefined = undefined;

    const noteHoldStartTimeSeconds = note.just;
    let noteHoldEndTimeSeconds = note.just;

    if (note.holds.length > 0) {
      const lastHoldTarget = note.holds[note.holds.length - 1];
      if (lastHoldTarget.type === 'beat') {
        noteHoldEndTimeSeconds = lastHoldTarget.beat;
      } else {
        const lastLinkedNote = allParsedNotesMap.get(lastHoldTarget.uid);
        if (lastLinkedNote) {
          noteHoldEndTimeSeconds = lastLinkedNote.just;
        } else {
          noteHoldEndTimeSeconds = note.just;
        }
      }
    }
    const totalNoteHoldDurationSeconds = noteHoldEndTimeSeconds - noteHoldStartTimeSeconds;
    const overallHoldSequenceInitialBeatActual = ChartRenderer.convertChartSecondsToBeat(
      note.just,
      this.bpms,
      this.chartOffset
    );
    const overallHoldSequenceFinalBeatActual = ChartRenderer.convertChartSecondsToBeat(
      noteHoldEndTimeSeconds,
      this.bpms,
      this.chartOffset
    );

    for (const holdEnd of note.holds) {
      let segmentEndTimeSeconds: number;
      let linkedNote: ParsedNote | undefined = undefined;

      if (holdEnd.type === 'beat') {
        segmentEndTimeSeconds = holdEnd.beat;
      } else {
        const chainedNote = allParsedNotesMap.get(holdEnd.uid);
        if (!chainedNote) {
          return undefined;
        }
        segmentEndTimeSeconds = chainedNote.just;
        linkedNote = chainedNote;
      }

      let lanesForThisSegmentEndL: number;
      let lanesForThisSegmentEndR: number;

      if (linkedNote) {
        lanesForThisSegmentEndL = this._getNormalizedLanePosition(linkedNote.flags.L1);
        lanesForThisSegmentEndR = this._getNormalizedLanePosition(linkedNote.flags.R1);
      } else if (segmentEndTimeSeconds <= currentSegmentStartTimeSeconds) {
        lanesForThisSegmentEndL = currentSegmentStartLaneL;
        lanesForThisSegmentEndR = currentSegmentStartLaneR;
      } else if (totalNoteHoldDurationSeconds < FLOAT_COMPARISON_EPSILON) {
        lanesForThisSegmentEndL = overallFinalLaneL;
        lanesForThisSegmentEndR = overallFinalLaneR;
      } else if (segmentEndTimeSeconds <= noteHoldStartTimeSeconds) {
        lanesForThisSegmentEndL = overallInitialLaneL;
        lanesForThisSegmentEndR = overallInitialLaneR;
      } else if (segmentEndTimeSeconds >= noteHoldEndTimeSeconds) {
        lanesForThisSegmentEndL = overallFinalLaneL;
        lanesForThisSegmentEndR = overallFinalLaneR;
      } else {
        const fraction =
          (segmentEndTimeSeconds - noteHoldStartTimeSeconds) / totalNoteHoldDurationSeconds;
        lanesForThisSegmentEndL =
          overallInitialLaneL + (overallFinalLaneL - overallInitialLaneL) * fraction;
        lanesForThisSegmentEndR =
          overallInitialLaneR + (overallFinalLaneR - overallInitialLaneR) * fraction;
      }
      const segmentInitialStartBeatActual = ChartRenderer.convertChartSecondsToBeat(
        currentSegmentStartTimeSeconds,
        this.bpms,
        this.chartOffset
      );
      const segmentFinalEndBeatActual = ChartRenderer.convertChartSecondsToBeat(
        segmentEndTimeSeconds,
        this.bpms,
        this.chartOffset
      );

      this._drawIndividualHoldSegment(
        {
          segmentOwningNoteUidForDebugText: note.uid,
          thisSegmentStartTimeSecondsForDebugText: currentSegmentStartTimeSeconds,

          thisSegmentActualStartBeat: segmentInitialStartBeatActual,
          thisSegmentActualEndBeat: segmentFinalEndBeatActual,
          thisSegmentStartLaneL: currentSegmentStartLaneL,
          thisSegmentStartLaneR: currentSegmentStartLaneR,
          thisSegmentEndLaneL: lanesForThisSegmentEndL,
          thisSegmentEndLaneR: lanesForThisSegmentEndR,

          noteWhoseHoldsDefineTicks: note,
          allParsedNotesMapForTicks: allParsedNotesMap,

          overallHoldSequenceInitialBeat: overallHoldSequenceInitialBeatActual,
          overallHoldSequenceFinalBeat: overallHoldSequenceFinalBeatActual,
          overallHoldSequenceInitialLaneL: overallInitialLaneL,
          overallHoldSequenceInitialLaneR: overallInitialLaneR,
          overallHoldSequenceFinalLaneL: overallFinalLaneL,
          overallHoldSequenceFinalLaneR: overallFinalLaneR
        },
        container
      );

      currentSegmentStartTimeSeconds = segmentEndTimeSeconds;
      currentSegmentStartLaneL = lanesForThisSegmentEndL;
      currentSegmentStartLaneR = lanesForThisSegmentEndR;

      if (linkedNote) {
        if (!nextNoteInChain) {
          nextNoteInChain = linkedNote;
        }
      }
    }
    return nextNoteInChain;
  }
  private _getNoteHeadsToRender(note: ParsedNote, isFirstInChain: boolean): ParsedNote[] {
    const headsToRender: ParsedNote[] = [];

    if (note.flags.noteType !== 'Hold' || isFirstInChain || note.holds.length === 0) {
      headsToRender.push(note);
    }

    if (note.flags.noteType === 'Hold' && note.holds.length > 0) {
      const lastHoldLink = note.holds[note.holds.length - 1];
      if (lastHoldLink.type === 'beat') {
        const endHeadFlags: Flags = {
          noteType: 'Hold',
          L1: note.flags.L2,
          R1: note.flags.R2,
          L2: 0,
          R2: 0
        };
        headsToRender.push({
          uid: note.uid,
          just: lastHoldLink.beat,
          holds: [],
          flags: endHeadFlags
        });
      }
    }
    return headsToRender;
  }

  private _drawMeasureComboCounts(maxBeat: number, numerator: number, container: Container) {
    if (!this.comboCounter) {
      return;
    }

    for (
      let measureLineBeat = numerator;
      measureLineBeat <= maxBeat + FLOAT_COMPARISON_EPSILON; // Epsilon for float comparison
      measureLineBeat += numerator
    ) {
      const timeInSecondsForMeasureLine = ChartRenderer.convertBeatToChartSeconds(
        measureLineBeat,
        this.bpms,
        this.chartOffset
      );
      const comboCountUpToThisMeasure = this.comboCounter.getCumulativeComboUpToBeat(
        timeInSecondsForMeasureLine
      );

      if (comboCountUpToThisMeasure > 0) {
        const comboCountStyle = new TextStyle({
          fontFamily: 'Arial',
          fontSize: 12,
          fill: '0xffffff',
          align: 'left'
        });
        const comboText = new Text({
          text: String(comboCountUpToThisMeasure),
          style: comboCountStyle
        });

        const columnX = this._getBeatXOffset(measureLineBeat);
        const lineY_gridTop = this._getBeatYOffset(measureLineBeat);
        const lineY_center = lineY_gridTop + GRID_LINE_THICKNESS / 2;
        comboText.anchor.set(0, 0.5);
        comboText.x = columnX + this.laneWidth + COMBO_COUNT_PADDING;
        comboText.y = lineY_center;
        container.addChild(comboText);

        // Add timestamp text below combo count
        const timestampStyle = new TextStyle({
          fontFamily: 'Arial',
          fontSize: 10,
          fill: '0xaaaaaa',
          align: 'left'
        });
        const minutes = Math.floor(timeInSecondsForMeasureLine / 60);
        const seconds = Math.floor(timeInSecondsForMeasureLine % 60);
        const timestampText = new Text({
          text: `${minutes}:${seconds.toString().padStart(2, '0')}`,
          style: timestampStyle
        });
        timestampText.anchor.set(0, 0.5);
        timestampText.x = columnX + this.laneWidth + COMBO_COUNT_PADDING;
        timestampText.y = lineY_center + 14; // Position below combo count
        container.addChild(timestampText);
      }
    }
  }

  private _drawBpmInfo(container: Container) {
    const sortedBpms = [...this.bpms].sort((a, b) => a.Time - b.Time);
    const renderedMarkers = new Set<string>();

    let bpmAtChartStartTimeZero = 120;
    if (sortedBpms.length > 0) {
      let foundEffectiveStartBpm = false;
      for (let i = sortedBpms.length - 1; i >= 0; i--) {
        if (sortedBpms[i].Time <= this.chartOffset + FLOAT_COMPARISON_EPSILON) {
          bpmAtChartStartTimeZero = sortedBpms[i].Bpm;
          foundEffectiveStartBpm = true;
          break;
        }
      }
      if (!foundEffectiveStartBpm && sortedBpms.length > 0) {
        bpmAtChartStartTimeZero = sortedBpms[0].Bpm;
      }
    }

    this._renderSingleBpmMarker(bpmAtChartStartTimeZero, 0, container, renderedMarkers);

    let bpmActiveBeforeCurrentEvent = bpmAtChartStartTimeZero;
    for (const bpmEvent of sortedBpms) {
      const chartRelativeTimeSeconds = bpmEvent.Time - this.chartOffset;

      if (chartRelativeTimeSeconds > FLOAT_COMPARISON_EPSILON) {
        if (Math.abs(bpmEvent.Bpm - bpmActiveBeforeCurrentEvent) > FLOAT_COMPARISON_EPSILON) {
          this._renderSingleBpmMarker(
            bpmEvent.Bpm,
            chartRelativeTimeSeconds,
            container,
            renderedMarkers
          );
        }
      }
      bpmActiveBeforeCurrentEvent = bpmEvent.Bpm;
    }
  }

  private _renderSingleBpmMarker(
    bpmValue: number,
    chartRelativeTimeSeconds: number,
    container: Container,
    renderedMarkers: Set<string>
  ) {
    const beatForBpm = ChartRenderer.convertChartSecondsToBeat(
      chartRelativeTimeSeconds,
      this.bpms,
      this.chartOffset
    );
    if (
      beatForBpm < -FLOAT_COMPARISON_EPSILON &&
      Math.abs(chartRelativeTimeSeconds) > FLOAT_COMPARISON_EPSILON
    )
      return;
    const displayBeat = Math.max(0, beatForBpm);

    const markerKey = `${displayBeat.toFixed(3)}_${bpmValue.toFixed(0)}`;
    if (renderedMarkers.has(markerKey)) return;

    const columnX = this._getBeatXOffset(displayBeat);
    const lineY_gridTop = this._getBeatYOffset(displayBeat);
    const lineY_center = lineY_gridTop + GRID_LINE_THICKNESS / 2;

    const bpmLine = new Graphics();
    bpmLine.rect(columnX, lineY_gridTop, this.laneWidth, GRID_LINE_THICKNESS).fill(0xff6600);
    container.addChild(bpmLine);

    const bpmText = new Text({ text: `${bpmValue.toFixed(0)} BPM`, style: this._bpmTextStyle });
    bpmText.anchor.set(0, 1);
    bpmText.x = columnX + this.laneWidth + COMBO_COUNT_PADDING;
    bpmText.y = lineY_center - 8;
    container.addChild(bpmText);
    renderedMarkers.add(markerKey);
  }

  private _drawNoteChain(
    startingNote: ParsedNote,
    allParsedNotesMap: Map<number, ParsedNote>,
    notesContainer: Container
  ) {
    let currentNote: ParsedNote | undefined = startingNote;
    let isFirstInChain = true;

    while (currentNote) {
      const noteToDraw = currentNote;
      let nextLinkedHoldNoteViaUID: ParsedNote | undefined = undefined;

      if (noteToDraw.flags.noteType === 'Hold' && noteToDraw.holds.length > 0) {
        nextLinkedHoldNoteViaUID = this._drawHoldSegmentsForNote(
          noteToDraw,
          allParsedNotesMap,
          notesContainer
        );
      }

      const headsToDraw = this._getNoteHeadsToRender(noteToDraw, isFirstInChain);
      headsToDraw.forEach((headInfo) => {
        this._drawSingleNoteOrHoldHead(headInfo, notesContainer);
      });

      currentNote = nextLinkedHoldNoteViaUID;
      isFirstInChain = false;
    }
  }

  private _getCumulativeScaledOffset(fromBeat: number, toBeat: number): number {
    if (fromBeat >= toBeat) return 0;

    let offset = 0;
    let currentBeat = fromBeat;

    const sortedBpms = [...this.bpms].sort((a, b) => a.Time - b.Time);

    while (currentBeat < toBeat - FLOAT_COMPARISON_EPSILON) {
      const currentTimeSeconds = ChartRenderer.convertBeatToChartSeconds(
        currentBeat,
        this.bpms,
        this.chartOffset
      );
      const absoluteTime = currentTimeSeconds + this.chartOffset;

      let currentBpm = this.referenceBpm;
      let nextBpmChangeTime = Infinity;

      for (let i = 0; i < sortedBpms.length; i++) {
        if (sortedBpms[i].Time <= absoluteTime + FLOAT_COMPARISON_EPSILON) {
          currentBpm = sortedBpms[i].Bpm;
        } else {
          nextBpmChangeTime = sortedBpms[i].Time;
          break;
        }
      }

      if (currentBpm === 0) {
        currentBeat = Math.ceil(currentBeat);
        continue;
      }

      let nextBpmChangeBeat = toBeat;
      if (nextBpmChangeTime < Infinity) {
        nextBpmChangeBeat = ChartRenderer.convertChartSecondsToBeat(
          nextBpmChangeTime - this.chartOffset,
          this.bpms,
          this.chartOffset
        );
      }

      const segmentEndBeat = Math.min(toBeat, nextBpmChangeBeat);
      const beatDelta = segmentEndBeat - currentBeat;

      if (beatDelta > FLOAT_COMPARISON_EPSILON) {
        const scale = this.referenceBpm / currentBpm;
        const baseHeight = this.beatSpacing + GRID_LINE_THICKNESS;
        offset += beatDelta * baseHeight * scale;
      }

      currentBeat = segmentEndBeat;
    }

    return offset;
  }

  private _computeColumnBoundaries(maxBeat: number): number[] {
    const boundaries: number[] = [0];
    let currentBeat = 0;

    while (currentBeat < maxBeat - FLOAT_COMPARISON_EPSILON) {
      const nextBoundary = this._findNextColumnBoundary(currentBeat, maxBeat);
      boundaries.push(nextBoundary);
      currentBeat = nextBoundary;
    }

    return boundaries;
  }

  private _findNextColumnBoundary(fromBeat: number, maxBeat: number): number {
    const targetHeight = this._targetColumnHeight;
    let low = fromBeat;
    let high = maxBeat;

    const heightToMax = this._getCumulativeScaledOffset(fromBeat, maxBeat);
    if (heightToMax <= targetHeight + FLOAT_COMPARISON_EPSILON) {
      return maxBeat;
    }

    for (let i = 0; i < 50; i++) {
      const mid = (low + high) / 2;
      const heightToMid = this._getCumulativeScaledOffset(fromBeat, mid);

      if (Math.abs(heightToMid - targetHeight) < 0.1) {
        return mid;
      }

      if (heightToMid < targetHeight) {
        low = mid;
      } else {
        high = mid;
      }
    }

    return (low + high) / 2;
  }

  private _getColumnIndexForBeat(beat: number): number {
    for (let i = 0; i < this._columnBoundaries.length - 1; i++) {
      if (beat >= this._columnBoundaries[i] && beat < this._columnBoundaries[i + 1]) {
        return i;
      }
    }
    return Math.max(0, this._columnBoundaries.length - 2);
  }

  private _getBeatYOffset(beat: number) {
    const columnIndex = this._getColumnIndexForBeat(beat);
    const columnStartBeat = this._columnBoundaries[columnIndex];

    const offsetFromColumnStart = this._getCumulativeScaledOffset(columnStartBeat, beat);

    return this._targetColumnHeight - offsetFromColumnStart + CHART_PADDING;
  }

  private _getBeatXOffset(beat: number) {
    const columnIndex = this._getColumnIndexForBeat(beat);
    return columnIndex * (this.laneWidth + COLUMN_SPACING) + CHART_PADDING;
  }

  get canvas() {
    return this.app.canvas;
  }

  public static convertBeatToChartSeconds(
    targetBeat: number,
    bpms: ChartData['Bpms'],
    chartOffsetSeconds: number,
    _beatsInfo?: ChartData['Beats']
  ): number {
    if (Math.abs(targetBeat) < FLOAT_COMPARISON_EPSILON) return 0;

    let currentBeatCount = 0;
    let currentTimePointer = chartOffsetSeconds;
    const sortedBpms = [...bpms].sort((a, b) => a.Time - b.Time);

    let currentBpm = 120;
    let bpmEventIndex = 0;

    let initialBpmSet = false;
    for (let i = 0; i < sortedBpms.length; i++) {
      if (sortedBpms[i].Time <= currentTimePointer + FLOAT_COMPARISON_EPSILON) {
        currentBpm = sortedBpms[i].Bpm;
        bpmEventIndex = i + 1;
        initialBpmSet = true;
      } else {
        if (!initialBpmSet) {
          // First BPM event is after chartOffsetSeconds
          currentBpm = sortedBpms[0]?.Bpm || 120;
          bpmEventIndex = 0;
        }
        break;
      }
    }
    if (!initialBpmSet && sortedBpms.length > 0) {
      currentBpm = sortedBpms[0].Bpm;
      bpmEventIndex = 0;
    } else if (sortedBpms.length === 0) {
      currentBpm = 120;
    }

    while (currentBeatCount < targetBeat - FLOAT_COMPARISON_EPSILON) {
      if (currentBpm === 0) {
        if (
          bpmEventIndex < sortedBpms.length &&
          sortedBpms[bpmEventIndex].Time > currentTimePointer
        ) {
          currentTimePointer = sortedBpms[bpmEventIndex].Time;
          currentBpm = sortedBpms[bpmEventIndex].Bpm;
          bpmEventIndex++;
        } else {
          return Infinity;
        }
        continue;
      }

      const secondsPerBeat = 60 / currentBpm;
      const timeOfNextBpmChange =
        bpmEventIndex < sortedBpms.length ? sortedBpms[bpmEventIndex].Time : Infinity;
      const beatsInSegmentUntilNextChange =
        (timeOfNextBpmChange - currentTimePointer) / secondsPerBeat;

      if (
        currentBeatCount + beatsInSegmentUntilNextChange >=
        targetBeat - FLOAT_COMPARISON_EPSILON
      ) {
        currentTimePointer += (targetBeat - currentBeatCount) * secondsPerBeat;
        currentBeatCount = targetBeat;
      } else {
        currentTimePointer = timeOfNextBpmChange;
        currentBeatCount += beatsInSegmentUntilNextChange;
        currentBpm = sortedBpms[bpmEventIndex].Bpm;
        bpmEventIndex++;
      }
    }
    return currentTimePointer - chartOffsetSeconds;
  }

  public static convertChartSecondsToBeat(
    chartRelativeSeconds: number,
    bpms: ChartData['Bpms'],
    chartOffsetSeconds: number
  ): number {
    const absoluteTargetSeconds = chartRelativeSeconds + chartOffsetSeconds;
    let currentBeat = 0;
    let currentTimePointer = chartOffsetSeconds;

    const sortedBpms = [...bpms].sort((a, b) => a.Time - b.Time);

    let currentBpm: number;
    let nextBpmEventIndex = 0;

    if (sortedBpms.length > 0) {
      currentBpm = sortedBpms[0].Bpm;
      for (let i = 0; i < sortedBpms.length; ++i) {
        if (sortedBpms[i].Time <= currentTimePointer + FLOAT_COMPARISON_EPSILON) {
          currentBpm = sortedBpms[i].Bpm;
          nextBpmEventIndex = i + 1;
        } else {
          break;
        }
      }
    } else {
      currentBpm = 120;
    }

    while (currentTimePointer < absoluteTargetSeconds) {
      if (currentBpm === 0) {
        let nextChangeTime = absoluteTargetSeconds;
        if (nextBpmEventIndex < sortedBpms.length) {
          nextChangeTime = Math.min(nextChangeTime, sortedBpms[nextBpmEventIndex].Time);
        }
        currentTimePointer = nextChangeTime;
        if (
          nextBpmEventIndex < sortedBpms.length &&
          currentTimePointer >= sortedBpms[nextBpmEventIndex].Time
        ) {
          currentBpm = sortedBpms[nextBpmEventIndex].Bpm;
          nextBpmEventIndex++;
        }
        continue;
      }

      const secondsPerBeat = 60 / currentBpm;
      let timeOfNextBpmChange = absoluteTargetSeconds;

      if (nextBpmEventIndex < sortedBpms.length) {
        timeOfNextBpmChange = sortedBpms[nextBpmEventIndex].Time;
      }

      const endOfThisSegment = Math.min(absoluteTargetSeconds, timeOfNextBpmChange);
      const timeDeltaInSegment = endOfThisSegment - currentTimePointer;

      if (timeDeltaInSegment > FLOAT_COMPARISON_EPSILON) {
        currentBeat += timeDeltaInSegment / secondsPerBeat;
      }

      currentTimePointer = endOfThisSegment;

      if (
        currentTimePointer >= timeOfNextBpmChange - FLOAT_COMPARISON_EPSILON &&
        nextBpmEventIndex < sortedBpms.length
      ) {
        currentBpm = sortedBpms[nextBpmEventIndex].Bpm;
        nextBpmEventIndex++;
      }
    }
    return currentBeat;
  }

  private static _initializeTiming(Bpms: ChartData['Bpms'], Beats: ChartData['Beats'] | undefined) {
    const initialBpmValue = Bpms[0]?.Bpm || 120;
    const firstBeatInfo = Beats?.[0];
    const initialBeatSigTuple: [number, number] | undefined = firstBeatInfo
      ? [firstBeatInfo.Numerator, firstBeatInfo.Denominator]
      : undefined;

    return { initialBpmValue, initialBeatSigTuple };
  }

  private static _buildHoldNotesByTimeMap(notes: Note[]): Map<string, Note[]> {
    const holdNotesByTime = new Map<string, Note[]>();
    for (const note of notes) {
      if (parseFlags(note.Flags).noteType === 'Hold') {
        const keyTime = ChartRenderer.getHoldNoteKey(note.just);
        const notesAtTime = holdNotesByTime.get(keyTime) || [];
        notesAtTime.push(note);
        holdNotesByTime.set(keyTime, notesAtTime);
      }
    }
    return holdNotesByTime;
  }

  static getHoldNoteKey(just: string) {
    return (Math.ceil(Number(just) * HOLD_NOTE_KEY_PRECISION) / HOLD_NOTE_KEY_PRECISION).toFixed(
      HOLD_NOTE_KEY_DIGITS
    );
  }

  private static _processRawNotes(notes: Note[], holdNotesByTime: Map<string, Note[]>) {
    const noteUIDs: number[] = [];
    const noteFlagsNums: number[] = [];
    const noteJustSeconds: number[] = [];
    const noteResolvedHolds: number[][] = [];

    const allEventSeconds: number[] = [];
    const allChainedNoteUIDs = new Set<number>();

    for (const rawNote of notes) {
      const currentRawNoteParsedFlags = parseFlags(rawNote.Flags);
      const justSeconds = Number(rawNote.just);
      allEventSeconds.push(justSeconds);
      const resolvedHolds: number[] = [];

      for (let i = 0; i < rawNote.holds.length; i++) {
        const holdTimeSecStr = rawNote.holds[i];
        const isLastHoldSegment = i === rawNote.holds.length - 1;

        let holdLinkValue: number;
        let foundLinkUID = -1;

        if (isLastHoldSegment && currentRawNoteParsedFlags.noteType === 'Hold') {
          const keyTime = ChartRenderer.getHoldNoteKey(holdTimeSecStr);
          const potentialTargetRawNotes = holdNotesByTime.get(keyTime);

          if (potentialTargetRawNotes) {
            for (const targetRawNote of potentialTargetRawNotes) {
              if (allChainedNoteUIDs.has(targetRawNote.Uid)) {
                continue;
              }
              const targetRawNoteParsedFlags = parseFlags(targetRawNote.Flags);

              if (
                currentRawNoteParsedFlags.L2 === targetRawNoteParsedFlags.L1 &&
                currentRawNoteParsedFlags.R2 === targetRawNoteParsedFlags.R1
              ) {
                foundLinkUID = targetRawNote.Uid;
                break;
              }
            }
          }
        }
        if (foundLinkUID !== -1) {
          holdLinkValue = -foundLinkUID;
          allChainedNoteUIDs.add(foundLinkUID);
        } else {
          const holdEndSeconds = Number(holdTimeSecStr);
          holdLinkValue = holdEndSeconds;
          allEventSeconds.push(holdEndSeconds);
        }
        resolvedHolds.push(holdLinkValue);
      }

      noteUIDs.push(rawNote.Uid);
      noteFlagsNums.push(rawNote.Flags);
      noteJustSeconds.push(justSeconds);
      noteResolvedHolds.push(resolvedHolds);
    }

    return {
      noteUIDs,
      noteFlagsNums,
      noteJustSeconds,
      noteResolvedHolds,
      allEventSeconds,
      allChainedNoteUIDs
    };
  }

  public static preProcessChartData(chartData: ChartData): PreProcessedChartFileSoA {
    const { Notes, Bpms, Beats, Offset } = chartData;

    const { initialBpmValue, initialBeatSigTuple } = this._initializeTiming(Bpms, Beats);

    const holdNotesByTime = this._buildHoldNotesByTimeMap(Notes);

    const {
      noteUIDs,
      noteFlagsNums,
      noteJustSeconds,
      noteResolvedHolds,
      allEventSeconds,
      allChainedNoteUIDs
    } = this._processRawNotes(Notes, holdNotesByTime);

    const allEventBeatsForMax = allEventSeconds.map((sec) =>
      ChartRenderer.convertChartSecondsToBeat(sec, Bpms, Offset)
    );
    const maxBeatValueFromEvents =
      allEventBeatsForMax.length > 0 ? Math.max(0, ...allEventBeatsForMax) : 0;
    const maxBeat = Math.ceil(maxBeatValueFromEvents / 4) * 4;

    const rootNoteUIDs: number[] = Notes.filter(
      (note: Note) => !allChainedNoteUIDs.has(note.Uid)
    ).map((note: Note) => note.Uid);

    return [
      noteUIDs,
      noteFlagsNums,
      noteJustSeconds,
      noteResolvedHolds,
      initialBpmValue,
      initialBeatSigTuple,
      maxBeat,
      rootNoteUIDs,
      Bpms,
      Offset
    ];
  }
}
